import { FC } from 'react';
import styled from '@emotion/styled';
import {
  Card,
  Divider,
  ProgressWithLabel,
  SearchField,
  Spacing,
  StyledDiv,
  Typography,
} from '@taleemabad/dsm/components';
import { BreakpointSizes, mq } from '@taleemabad/dsm/themes';

export interface CoachAnalytics {
  id: number;
  name: string;
  totalPlannedVisits: number;
  observationsConducted: number;
  observationsRemaining: number;
  progressPercentage: number;
}

export interface RmHomeScreenProps {
  dateRange: string;
  rmName: string;
  completionPercentage: number;
  coaches: CoachAnalytics[];
  searchValue: string;
  onSearchChange: (value: string) => void;
}

export const RmHomeScreen: FC<RmHomeScreenProps> = ({
  dateRange,
  rmName,
  completionPercentage,
  coaches,
  searchValue,
  onSearchChange,
}) => {
  // Filter coaches by search value (case-insensitive)
  const filteredCoaches = coaches.filter((coach) =>
    coach.name.toLowerCase().includes(searchValue.toLowerCase()),
  );

  return (
    <StyledDiv flexDirection="column" width="100%">
      <Spacing marginTop="xl" marginBottom="xl">
        <StyledDiv flexDirection="column" alignItem="center">
          <Typography variant="textBodyMedium" textAlign="center">
            Analytics for observations between
          </Typography>
          <Typography variant="textTitleMedium" textAlign="center">
            {dateRange}
          </Typography>

          <Spacing marginTop="xxxl" />

          <Typography variant="textTitleMedium" textAlign="center">
            {`${rmName}'s Total Schools`}
          </Typography>
          <Spacing marginTop="md" />
          <ProgressWithLabel
            value={completionPercentage}
            text={`${completionPercentage}%`}
            size="lg"
          />
        </StyledDiv>

        <Spacing marginTop="xxl" />
        <Divider />
        <Spacing marginBottom="xxxl" />

        <StyledSearchFieldWrapper>
          <SearchField
            placeholder="Search Coach"
            value={searchValue}
            onChange={onSearchChange}
          />
        </StyledSearchFieldWrapper>

        <Spacing marginTop="xl" />

        <StyledCardsContainer>
          {filteredCoaches.length === 0 ? (
            <Typography variant="textBodyMedium" textAlign="center">
              No coaches found.
            </Typography>
          ) : (
            filteredCoaches.map((coach) => {
              return (
                <Card key={coach.id} variant="outline" minHeight="60px">
                  <StyledDiv justifyContent="space-between" alignItem="center">
                    <StyledDiv flexDirection="column">
                      <Typography variant="textTitleMedium">
                        {coach.name}
                      </Typography>
                      <Spacing marginTop="md" />
                      {/* TODO: may need it later */}
                      {/* <Typography variant="textBodyMedium">
                        Total Planned Visits: {coach.totalPlannedVisits}
                      </Typography> */}
                      <Typography variant="textBodyMedium">
                        Observations Conducted: {coach.observationsConducted}
                      </Typography>
                      <Typography variant="textBodyMedium">
                        Observations Remaining: {coach.observationsRemaining}
                      </Typography>
                    </StyledDiv>
                    <ProgressWithLabel
                      value={coach.progressPercentage}
                      text={`${coach.progressPercentage}%`}
                      size="sm"
                    />
                  </StyledDiv>
                </Card>
              );
            })
          )}
        </StyledCardsContainer>
      </Spacing>
    </StyledDiv>
  );
};

const StyledCardsContainer = styled(StyledDiv)`
  width: 100%;
  flex-direction: column;
  gap: var(--rk-spacing-md);

  ${mq(BreakpointSizes.desktop)} {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: var(--rk-spacing-xl);
  }
`;

const StyledSearchFieldWrapper = styled.div`
  width: 100%;
  ${mq(BreakpointSizes.desktop)} {
    width: 50%;
  }
`;
